import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import { ArrowRight, Check } from "lucide-react";
import { useLang } from "../App";
import { membersI18n } from "../i18n";
import PageHeader from "../components/PageHeader";
import { PasswordInput } from "../components/PasswordInput";

const API = process.env.REACT_APP_BACKEND_URL;

export default function ResetPassword() {
  const { lang } = useLang();
  const m = membersI18n[lang];
  const { token } = useParams();
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [status, setStatus] = useState("idle");
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (pw !== pw2) {
      setError(m.mismatch);
      return;
    }
    setStatus("sending");
    try {
      const res = await fetch(`${API}/api/members/reset-password`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ token, password: pw }),
      });
      if (!res.ok) throw new Error("invalid");
      setStatus("done");
    } catch {
      setError(m.resetInvalid);
      setStatus("idle");
    }
  };

  const inputCls = "flen-input w-full";
  const labelCls = "mb-2 block text-[11px] font-medium uppercase tracking-[0.25em] text-ink/50";

  return (
    <div data-testid="reset-password-page" className="min-h-screen bg-ivory text-ink">
      <PageHeader active="members" />
      <section className="mx-auto max-w-xl px-6 pb-28 pt-16 md:pt-24">
        <p className="text-[11px] font-medium uppercase tracking-[0.3em] text-bronze">{m.navLabel}</p>
        <h1 className="mt-6 font-serif text-4xl font-medium leading-tight sm:text-5xl">{m.resetTitle}</h1>

        {status === "done" ? (
          <div data-testid="reset-success" className="mt-12 border border-bronze/40 p-10">
            <span className="flex h-12 w-12 items-center justify-center border border-bronze text-bronze">
              <Check size={20} strokeWidth={1.5} />
            </span>
            <p className="mt-8 text-sm font-light leading-relaxed text-ink/65">{m.resetDone}</p>
            <Link
              to="/espace"
              data-testid="reset-to-login"
              className="group mt-8 inline-flex items-center gap-3 text-[11px] font-semibold uppercase tracking-[0.25em] text-bronze transition-colors duration-300 hover:text-ink"
            >
              {m.backToLogin}
              <ArrowRight size={14} className="transition-transform duration-500 group-hover:translate-x-1" />
            </Link>
          </div>
        ) : (
          <form data-testid="reset-password-form" onSubmit={submit} className="mt-12 space-y-8">
            <div>
              <label htmlFor="np-1" className={labelCls}>{m.newPassword} * (min. 8)</label>
              <PasswordInput id="np-1" minLength={8} testid="reset-password-input" value={pw}
                onChange={(e) => setPw(e.target.value)} className={inputCls} />
            </div>
            <div>
              <label htmlFor="np-2" className={labelCls}>{m.confirmPassword} *</label>
              <PasswordInput id="np-2" minLength={8} testid="reset-password-confirm" value={pw2}
                onChange={(e) => setPw2(e.target.value)} className={inputCls} />
            </div>
            {error && <p data-testid="reset-error" className="text-sm text-red-800">{error}</p>}
            <button type="submit" data-testid="reset-submit" disabled={status === "sending"}
              className="group flex items-center gap-4 bg-bronze px-10 py-4 text-[11px] font-semibold uppercase tracking-[0.25em] text-ivory transition-colors duration-500 hover:bg-champagne hover:text-ink disabled:opacity-50">
              {status === "sending" ? "…" : m.resetBtn}
              <ArrowRight size={15} className="transition-transform duration-500 group-hover:translate-x-1.5" />
            </button>
          </form>
        )}
      </section>
    </div>
  );
}
